import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import type { SalesByReportItem } from "../../../../graphql/types"
import { flatten, flattenDeep, includes, map } from "lodash"
import { recursion } from "../../../../utils/helpers"
import type { FiltersObject } from "../../../filters/types"
import type { CategoriesTableDataItem } from "../../categories/itemsCategories/table/CategoriesTable"
import { getUnit } from "../../../../utils/units"
import { isCateringType } from "../../categories/utils"
import { plural } from "@figurepos/fgr-lib-business-logic/lib/formatters/plural"
import { SalesByItemsTotals } from "./types"

const getRootItem = (items: SalesByReportItem[]): Maybe<SalesByReportItem> => {
    return items.find(i => i.name.toLowerCase() === "root") || (items && items[0])
}

export const transformSalesByItemsData = (
    reportData: SalesByReportItem[],
    anyLocationsSelected: boolean,
    filters: FiltersObject
): SalesByReportItem[] => {
    const root = getRootItem(reportData)
    let rootChildren = root?.children ?? []

    if (!rootChildren || rootChildren.length === 0 || !anyLocationsSelected) {
        return []
    }

    if (filters?.view?.includes("percentageOfTotal")) {
        rootChildren = applyTotalPercentageFilter(rootChildren)
    }

    if (filters?.view?.includes("categories") && !filters.view.includes("items")) {
        rootChildren = onlyVisibleTypes(rootChildren, ["category"])
    }

    if (filters?.view?.includes("items") && !filters.view.includes("categories")) {
        rootChildren = onlyVisibleTypes(rootChildren, ["product", "option"])
    }

    if (filters?.view?.length === 0) {
        return []
    }

    return rootChildren ?? []
}

export const transformSalesByModifiersData = (
    reportData: SalesByReportItem[],
    anyLocationsSelected: boolean,
    filters: FiltersObject
): SalesByReportItem[] => {
    const root = getRootItem(reportData)
    let rootChildren = root?.children ?? []

    if (!rootChildren || rootChildren.length === 0 || !anyLocationsSelected) {
        return []
    }

    if (filters?.view?.includes("percentageOfTotal")) {
        rootChildren = applyTotalPercentageFilter(reportData)
    }

    if (filters?.view?.length === 0) {
        return []
    }

    return rootChildren
}

export const getTotalsFromData = (
    data: SalesByReportItem[],
    anyLocationsSelected: boolean,
    isLoading: boolean
): SalesByItemsTotals => {
    if (!anyLocationsSelected || isLoading) {
        return {
            quantityTotal: 0,
            quantityPercentageTotal: 0,
            revenueTotal: 0,
            revenuePercentageTotal: 0,
        }
    }

    const root = data.find(i => i.name.toLowerCase() === "root") ?? data?.[0] ?? null
    return {
        quantityTotal: root?.quantity || 0,
        quantityPercentageTotal: root?.quantityPercentage || 0,
        revenueTotal: root?.revenue || 0,
        revenuePercentageTotal: root?.revenuePercentage || 0,
    }
}

export const applyTotalPercentageFilter = (array: Maybe<SalesByReportItem[]>): SalesByReportItem[] => {
    return (array ?? [])
        .map(k => ({
            ...k,
            quantityPercentage: k.quantityPercentageTotal,
            revenuePercentage: k.revenuePercentageTotal,
            children: applyTotalPercentageFilter(k.children),
        }))
        .filter(Boolean)
}
export const onlyVisibleTypes = <T extends SalesByReportItem | CategoriesTableDataItem>(
    data: T[],
    visibleTypes: string[]
): T[] =>
    flattenDeep(
        map(
            data,
            recursion((callSelf, record) => {
                const includesChildren = flatten(map(record.children, callSelf))
                return includes(visibleTypes, record.type)
                    ? { ...record, children: includesChildren && includesChildren.length > 0 ? includesChildren : null }
                    : flatten(map(record.children, callSelf))
            })
        )
    ) as unknown as T[]

export const handleRenderQuantityCell = (record: SalesByReportItem): string => {
    if (record.unit && record.unitQuantity) {
        const unitQty = record.unitQuantity / 1000
        const unit = getUnit(record.unit).symbol || record.unit

        return `${unitQty} ${isCateringType(record.unit) ? plural(unit, unitQty) : unit}`
    } else {
        return "-"
    }
}
